import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import {
  DS_CLAIM_TYPE_CHECKBOX_SUBTYPE_PAIRS,
  normalizeDsClaimParametersResponse,
  syncDsClaimSubtypeControlsEnabled,
} from './query-extended-params.form';
import {
  DS_CLOSED_SUBSTATUS_CODES,
  DS_ERISA_EXCLUDE,
  DS_ERISA_INCLUDE,
  DS_INCIDENT_SUBSTATUS_CODES,
  DS_OPEN_SUBSTATUS_CODES,
  DS_VENDOR_EXCLUDE,
  DS_VENDOR_INCLUDE,
  normalizeDsErisaFromApi,
  normalizeDsVendorFromApi,
} from './ds-disability-legacy';
import { CheckboxComponent } from '../core/controls/checkbox.component';
import {
  dsMapClaimTypeOpts,
  dsPrependAllSubtypeOption,
} from './query-disability-lob.helpers';
import { WC_LEGACY_ALL_SUBTYPES_VALUE } from './wc-claim-type-labels';

const ALL_SUBSTATUS_CODES = [
  ...DS_OPEN_SUBSTATUS_CODES,
  ...DS_CLOSED_SUBSTATUS_CODES,
];
import { DatePickerComponent } from '../core/controls/date-picker.component';
import { SelectComponent } from '../core/controls/select.component';

@Component({
  selector: 'app-query-disability-lob',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CheckboxComponent,
    DatePickerComponent,
    SelectComponent,
  ],
  template: `
    <div class="tw-space-y-4 tw-max-w-[960px]">
      @if (claimTypesOnly || section === 'claim') {
        <div
          [formGroup]="dsTypesGroup"
          [class]="claimTypesOnly ? 'tw-space-y-3' : 'tw-pt-2 tw-space-y-3'"
        >
          @if (claimTypesOnly) {
            <h3 class="tw-font-semibold tw-text-lg">DS Claim Type:</h3>
          }
          @if (loading) {
            <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
              Loading disability claim types…
            </p>
          } @else {
            <div
              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4"
            >
              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
                <app-checkbox
                  [control]="$any(dsTypesGroup.controls['shortTerm'])"
                  label="Short Term"
                />
                <div>
                  <label
                    class="tw-sr-only"
                    for="qp-ds-claim-short-term-subtypes"
                    >Short term claim subtypes</label
                  >
                  <select
                    id="qp-ds-claim-short-term-subtypes"
                    multiple
                    class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    [formControl]="$any(dsTypesGroup.controls['shortTermIds'])"
                  >
                    @for (o of shortTermOpts; track o.value) {
                      <option [value]="o.value">{{ o.label }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
                <app-checkbox
                  [control]="$any(dsTypesGroup.controls['longTerm'])"
                  label="Long Term"
                />
                <div>
                  <label class="tw-sr-only" for="qp-ds-claim-long-term-subtypes"
                    >Long term claim subtypes</label
                  >
                  <select
                    id="qp-ds-claim-long-term-subtypes"
                    multiple
                    class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    [formControl]="$any(dsTypesGroup.controls['longTermIds'])"
                  >
                    @for (o of longTermOpts; track o.value) {
                      <option [value]="o.value">{{ o.label }}</option>
                    }
                  </select>
                </div>
              </div>

              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
                <app-checkbox
                  [control]="$any(dsTypesGroup.controls['supplemental'])"
                  label="Supplemental"
                />
                <div>
                  <label
                    class="tw-sr-only"
                    for="qp-ds-claim-supplemental-subtypes"
                    >Supplemental claim subtypes</label
                  >
                  <select
                    id="qp-ds-claim-supplemental-subtypes"
                    multiple
                    class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    [formControl]="$any(dsTypesGroup.controls['supplementIds'])"
                  >
                    @for (o of supplementOpts; track o.value) {
                      <option [value]="o.value">{{ o.label }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
                <app-checkbox
                  [control]="$any(dsTypesGroup.controls['voluntaryPay'])"
                  label="Voluntary Pay"
                />
                <div>
                  <label
                    class="tw-sr-only"
                    for="qp-ds-claim-voluntary-pay-subtypes"
                    >Voluntary pay claim subtypes</label
                  >
                  <select
                    id="qp-ds-claim-voluntary-pay-subtypes"
                    multiple
                    class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    [formControl]="
                      $any(dsTypesGroup.controls['voluntaryPayIds'])
                    "
                  >
                    @for (o of voluntaryPayOpts; track o.value) {
                      <option [value]="o.value">{{ o.label }}</option>
                    }
                  </select>
                </div>
              </div>

              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
                <app-checkbox
                  [control]="$any(dsTypesGroup.controls['paidLeave'])"
                  label="Paid Leave"
                />
                <div>
                  <label
                    class="tw-sr-only"
                    for="qp-ds-claim-paid-leave-subtypes"
                    >Paid leave claim subtypes</label
                  >
                  <select
                    id="qp-ds-claim-paid-leave-subtypes"
                    multiple
                    class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    [formControl]="$any(dsTypesGroup.controls['paidLeaveIds'])"
                  >
                    @for (o of paidLeaveOpts; track o.value) {
                      <option [value]="o.value">{{ o.label }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
                <app-checkbox
                  [control]="$any(dsTypesGroup.controls['incidentOnly'])"
                  label="Incident Only"
                />
                <div>
                  <label
                    class="tw-sr-only"
                    for="qp-ds-claim-incident-only-subtypes"
                    >Incident only claim subtypes</label
                  >
                  <select
                    id="qp-ds-claim-incident-only-subtypes"
                    multiple
                    class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    [formControl]="
                      $any(dsTypesGroup.controls['incidentOnlyIds'])
                    "
                  >
                    @for (o of incidentOnlyOpts; track o.value) {
                      <option [value]="o.value">{{ o.label }}</option>
                    }
                  </select>
                </div>
              </div>

              <div class="tw-col-span-1 md:tw-col-span-2 tw-pt-2">
                <div
                  class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-4 tw-bg-[var(--sed-white-color)]"
                >
                  <div class="tw-space-y-2">
                    <div
                      class="tw-text-sm tw-font-semibold tw-text-[var(--sed-black-60-color)]"
                    >
                      Open Substatus
                    </div>
                    @for (row of openSubstatusRows; track row.code) {
                      <app-checkbox
                        [control]="substatusCheckboxControl(row.code)"
                        [label]="row.label"
                      />
                    }
                  </div>
                  <div class="tw-space-y-2">
                    <div
                      class="tw-text-sm tw-font-semibold tw-text-[var(--sed-black-60-color)]"
                    >
                      Closed Substatus
                    </div>
                    @for (row of closedSubstatusRows; track row.code) {
                      <app-checkbox
                        [control]="substatusCheckboxControl(row.code)"
                        [label]="row.label"
                      />
                    }
                  </div>
                  <div class="tw-space-y-2">
                    <div
                      class="tw-text-sm tw-font-semibold tw-text-[var(--sed-black-60-color)]"
                    >
                      Incidents Substatus
                    </div>
                    @for (row of incidentSubstatusRows; track row.code) {
                      <app-checkbox
                        [control]="incidentCheckboxControl(row.code)"
                        [label]="row.label"
                      />
                    }
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }

      @if (!claimTypesOnly && section === 'misc') {
        <div
          class="tw-pt-2 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4"
          [formGroup]="dsMiscGroup"
        >
          <app-date-picker
            [control]="$any(dsMiscGroup.controls['dateBenefitFrom'])"
            label="Date of Benefit From"
          />
          <app-date-picker
            [control]="$any(dsMiscGroup.controls['dateBenefitThrough'])"
            label="Date of Benefit Through"
          />
          <app-select
            [control]="$any(dsMiscGroup.controls['erisa'])"
            [options]="erisaOptions"
            optionLabel="label"
            optionValue="value"
            label="ERISA Appeal"
          />
          <app-date-picker
            [control]="$any(dsMiscGroup.controls['decisionDueFrom'])"
            label="Decision Due From"
          />
          <app-date-picker
            [control]="$any(dsMiscGroup.controls['decisionDueThrough'])"
            label="Decision Due Through"
          />
          <app-date-picker
            [control]="$any(dsMiscGroup.controls['decisionMadeFrom'])"
            label="Decision Made From"
          />
          <app-date-picker
            [control]="$any(dsMiscGroup.controls['decisionMadeThrough'])"
            label="Decision Made Through"
          />
          <app-select
            [control]="$any(dsMiscGroup.controls['vendorReferral'])"
            [options]="vendorReferralOptions"
            optionLabel="label"
            optionValue="value"
            label="Vendor Referral"
          />
        </div>
      }
    </div>
  `,
})
export class QueryDisabilityLobComponent
  extends BaseComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ required: true }) extended!: FormGroup;
  /**
   * Main query form (General step). Used to mirror legacy disability substatus locks:
   * Open/Closed groups follow `claimOpen` / `claimClosed`; Incidents group is locked when
   * `reportIncludeType === 'ClaimOnly'` (see AdvancedMajorDisabilityClaimPageController / QueryWizardAdvancedMajorDSType.aspx.cs).
   */
  @Input() queryForm: FormGroup | null = null;
  @Input() queryUniqueKey: number | null = null;
  /** Claim Type step in the main wizard: claim types only (no misc). */
  @Input() claimTypesOnly = false;
  /** Advanced Disability: which section the parent tab strip shows (`misc` | `claim`). */
  @Input() section: 'misc' | 'claim' | null = null;

  loading = false;
  private lastLoadKey: string | null = null;
  private subs = new Subscription();
  private readonly substatusCheckboxByCode = new Map<
    string,
    FormControl<boolean>
  >();
  private readonly incidentCheckboxByCode = new Map<
    string,
    FormControl<boolean>
  >();
  private syncingSubstatusControls = false;

  shortTermOpts: { label: string; value: string }[] = [];
  longTermOpts: { label: string; value: string }[] = [];
  supplementOpts: { label: string; value: string }[] = [];
  voluntaryPayOpts: { label: string; value: string }[] = [];
  paidLeaveOpts: { label: string; value: string }[] = [];
  incidentOnlyOpts: { label: string; value: string }[] = [];

  readonly erisaOptions = [
    { label: 'Include Claims', value: DS_ERISA_INCLUDE },
    { label: 'Exclude Claims', value: DS_ERISA_EXCLUDE },
  ];

  readonly vendorReferralOptions = [
    { label: 'Include Claims', value: DS_VENDOR_INCLUDE },
    { label: 'Exclude Claims', value: DS_VENDOR_EXCLUDE },
  ];

  readonly openSubstatusRows: { code: string; label: string }[] = [
    { code: 'O|A', label: 'Approved' },
    { code: 'O|D', label: 'Denied' },
    { code: 'O|P', label: 'Pending' },
    { code: 'O|R', label: 'Reinstated' },
    { code: 'O|S', label: 'Suspended' },
    { code: 'O|T', label: 'Terminated' },
  ];

  readonly closedSubstatusRows: { code: string; label: string }[] = [
    { code: 'C|D', label: 'Denied' },
    { code: 'C|T', label: 'Terminated' },
  ];

  readonly incidentSubstatusRows: { code: string; label: string }[] = [
    { code: 'FM', label: 'Family Medical Leave' },
    { code: 'JA', label: 'JURIS Admin' },
  ];

  get dsTypesGroup(): FormGroup {
    return this.extended.get('dsTypes') as FormGroup;
  }

  get dsMiscGroup(): FormGroup {
    return this.extended.get('dsMisc') as FormGroup;
  }

  ngOnInit(): void {
    this.subs.add(
      this.dsTypesGroup.valueChanges.subscribe(() => {
        if (!this.loading) {
          this.extended
            .get('includeMajorDsTypes')
            ?.setValue(true, { emitEvent: false });
        }
      }),
    );
    this.subs.add(
      this.dsMiscGroup.valueChanges.subscribe(() => {
        if (!this.loading) {
          this.extended
            .get('includeMajorDsMisc')
            ?.setValue(true, { emitEvent: false });
        }
      }),
    );
    if (this.queryForm) {
      this.subs.add(
        this.queryForm.valueChanges.subscribe(() => {
          if (!this.loading) {
            this.scheduleRefreshDsSubstatusFromQueryForm();
          }
        }),
      );
    }
    this.wireDsClaimSubtypeEnableDisable();
    syncDsClaimSubtypeControlsEnabled(this.extended);
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['queryUniqueKey']) {
      this.lastLoadKey = null;
      void this.load();
    }
    if (changes['queryForm'] && this.queryForm && !this.loading) {
      this.scheduleRefreshDsSubstatusFromQueryForm();
    }
  }

  override ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Template must not create controls (side effects / NG0600). `load()` pre-creates all rows first.
   */
  substatusCheckboxControl(code: string): FormControl<boolean> {
    return this.substatusCheckboxByCode.get(code) as FormControl<boolean>;
  }

  incidentCheckboxControl(code: string): FormControl<boolean> {
    return this.incidentCheckboxByCode.get(code) as FormControl<boolean>;
  }

  /** Creates control + subscription only once; never call from template during first render before `load()`. */
  private ensureSubstatusCheckboxCreated(code: string): FormControl<boolean> {
    let c = this.substatusCheckboxByCode.get(code);
    if (c) {
      return c;
    }
    c = new FormControl(this.substatusIdsHas(code), { nonNullable: true });
    this.substatusCheckboxByCode.set(code, c);
    this.subs.add(
      c.valueChanges.subscribe((checked) => {
        if (this.syncingSubstatusControls || this.loading || c!.disabled) {
          return;
        }
        const cur = new Set(
          (this.dsTypesGroup.get('substatusIds')?.value ?? []) as string[],
        );
        if (checked) {
          cur.add(code);
        } else {
          cur.delete(code);
        }
        this.dsTypesGroup.get('substatusIds')?.setValue([...cur]);
        this.extended
          .get('includeMajorDsTypes')
          ?.setValue(true, { emitEvent: false });
      }),
    );
    return c;
  }

  private ensureIncidentCheckboxCreated(code: string): FormControl<boolean> {
    let c = this.incidentCheckboxByCode.get(code);
    if (c) {
      return c;
    }
    c = new FormControl(this.incidentIdsHas(code), { nonNullable: true });
    this.incidentCheckboxByCode.set(code, c);
    this.subs.add(
      c.valueChanges.subscribe((checked) => {
        if (this.syncingSubstatusControls || this.loading || c!.disabled) {
          return;
        }
        const cur = new Set(
          (this.dsTypesGroup.get('incidentSubstatusIds')?.value ??
            []) as string[],
        );
        if (checked) {
          cur.add(code);
        } else {
          cur.delete(code);
        }
        this.dsTypesGroup.get('incidentSubstatusIds')?.setValue([...cur]);
        this.extended
          .get('includeMajorDsTypes')
          ?.setValue(true, { emitEvent: false });
      }),
    );
    return c;
  }

  private ensureAllDsSubstatusCheckboxControlsCreated(): void {
    for (const code of ALL_SUBSTATUS_CODES) {
      this.ensureSubstatusCheckboxCreated(code);
    }
    for (const code of DS_INCIDENT_SUBSTATUS_CODES) {
      this.ensureIncidentCheckboxCreated(code);
    }
  }

  /** Avoid NG0600: never update forms/signals during template or synchronous valueChanges. */
  private scheduleRefreshDsSubstatusFromQueryForm(): void {
    queueMicrotask(() => this.refreshDsSubstatusAvailabilityFromQueryForm());
  }

  private substatusIdsHas(code: string): boolean {
    const ids = (this.dsTypesGroup.get('substatusIds')?.value ??
      []) as string[];
    return ids.includes(code);
  }

  private incidentIdsHas(code: string): boolean {
    const ids = (this.dsTypesGroup.get('incidentSubstatusIds')?.value ??
      []) as string[];
    return ids.includes(code);
  }

  /**
   * Legacy: when a group is not "Enabled" in binding XML, all its items stay checked and the
   * CheckBoxList is disabled. Flags come from General — claim status Open/Closed and report include type.
   */
  private refreshDsSubstatusAvailabilityFromQueryForm(): void {
    if (!this.queryForm) {
      this.enableAllDsSubstatusCheckboxControls();
      return;
    }

    const openInteractive = this.queryForm.get('claimOpen')?.value === true;
    const closedInteractive = this.queryForm.get('claimClosed')?.value === true;
    const incidentsInteractive =
      (this.queryForm.get('reportIncludeType')?.value ?? 'ClaimOnly') !==
      'ClaimOnly';

    this.syncingSubstatusControls = true;
    try {
      const subIds = new Set(
        (this.dsTypesGroup.get('substatusIds')?.value ?? []) as string[],
      );
      if (!openInteractive) {
        for (const code of DS_OPEN_SUBSTATUS_CODES) {
          subIds.add(code);
        }
      }
      if (!closedInteractive) {
        for (const code of DS_CLOSED_SUBSTATUS_CODES) {
          subIds.add(code);
        }
      }
      this.dsTypesGroup
        .get('substatusIds')
        ?.setValue([...subIds], { emitEvent: false });

      const incIds = new Set(
        (this.dsTypesGroup.get('incidentSubstatusIds')?.value ??
          []) as string[],
      );
      if (!incidentsInteractive) {
        for (const code of DS_INCIDENT_SUBSTATUS_CODES) {
          incIds.add(code);
        }
      }
      this.dsTypesGroup
        .get('incidentSubstatusIds')
        ?.setValue([...incIds], { emitEvent: false });

      for (const code of DS_OPEN_SUBSTATUS_CODES) {
        const c = this.substatusCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.substatusIdsHas(code), { emitEvent: false });
          if (!openInteractive) {
            c.disable({ emitEvent: false });
          } else {
            c.enable({ emitEvent: false });
          }
        }
      }
      for (const code of DS_CLOSED_SUBSTATUS_CODES) {
        const c = this.substatusCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.substatusIdsHas(code), { emitEvent: false });
          if (!closedInteractive) {
            c.disable({ emitEvent: false });
          } else {
            c.enable({ emitEvent: false });
          }
        }
      }
      for (const code of DS_INCIDENT_SUBSTATUS_CODES) {
        const c = this.incidentCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.incidentIdsHas(code), { emitEvent: false });
          if (!incidentsInteractive) {
            c.disable({ emitEvent: false });
          } else {
            c.enable({ emitEvent: false });
          }
        }
      }
    } finally {
      this.syncingSubstatusControls = false;
    }
  }

  private enableAllDsSubstatusCheckboxControls(): void {
    this.syncingSubstatusControls = true;
    try {
      for (const code of ALL_SUBSTATUS_CODES) {
        const c = this.substatusCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.substatusIdsHas(code), { emitEvent: false });
          c.enable({ emitEvent: false });
        }
      }
      for (const code of DS_INCIDENT_SUBSTATUS_CODES) {
        const c = this.incidentCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.incidentIdsHas(code), { emitEvent: false });
          c.enable({ emitEvent: false });
        }
      }
    } finally {
      this.syncingSubstatusControls = false;
    }
  }

  /** After API/form patch, keep auxiliary checkbox controls aligned (no duplicate `valueChanges`). */
  private syncSubstatusCheckboxControlsFromForm(): void {
    this.syncingSubstatusControls = true;
    try {
      for (const code of ALL_SUBSTATUS_CODES) {
        const c = this.substatusCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.substatusIdsHas(code), { emitEvent: false });
        }
      }
      for (const code of DS_INCIDENT_SUBSTATUS_CODES) {
        const c = this.incidentCheckboxByCode.get(code);
        if (c) {
          c.setValue(this.incidentIdsHas(code), { emitEvent: false });
        }
      }
    } finally {
      this.syncingSubstatusControls = false;
    }
  }

  /** Native multi-select + reactive forms: use control.disable() so UI matches WC (save uses getRawValue). */
  private wireDsClaimSubtypeEnableDisable(): void {
    for (const [cb, idsKey] of DS_CLAIM_TYPE_CHECKBOX_SUBTYPE_PAIRS) {
      const ctl = this.dsTypesGroup.get(cb);
      const ids = this.dsTypesGroup.get(idsKey);
      if (!ctl || !ids) {
        continue;
      }
      this.subs.add(
        ctl.valueChanges.subscribe((on: boolean) => {
          if (on) {
            ids.enable({ emitEvent: false });
            const cur = (ids.value ?? []) as string[];
            if (cur.length === 0) {
              ids.setValue([WC_LEGACY_ALL_SUBTYPES_VALUE], {
                emitEvent: false,
              });
            }
          } else {
            ids.setValue([], { emitEvent: false });
            ids.disable({ emitEvent: false });
          }
        }),
      );
    }
  }

  async load(): Promise<void> {
    const qk =
      this.queryUniqueKey && this.queryUniqueKey > 0
        ? this.queryUniqueKey
        : undefined;
    const loadKey = `${qk ?? 0}`;
    if (this.lastLoadKey === loadKey) {
      return;
    }
    this.lastLoadKey = loadKey;
    this.loading = true;
    try {
      const raw = await this.api.query.getDsClaimTypes(qk);
      const res = normalizeDsClaimParametersResponse(raw);
      this.shortTermOpts = dsPrependAllSubtypeOption(
        dsMapClaimTypeOpts(res.shortTermOptions ?? []),
      );
      this.longTermOpts = dsPrependAllSubtypeOption(
        dsMapClaimTypeOpts(res.longTermOptions ?? []),
      );
      this.supplementOpts = dsPrependAllSubtypeOption(
        dsMapClaimTypeOpts(res.supplementOptions ?? []),
      );
      this.voluntaryPayOpts = dsPrependAllSubtypeOption(
        dsMapClaimTypeOpts(res.voluntaryPayOptions ?? []),
      );
      this.paidLeaveOpts = dsPrependAllSubtypeOption(
        dsMapClaimTypeOpts(res.paidLeaveOptions ?? []),
      );
      this.incidentOnlyOpts = dsPrependAllSubtypeOption(
        dsMapClaimTypeOpts(res.incidentOnlyOptions ?? []),
      );

      const editing = qk != null && qk > 0;
      if (editing && res.types) {
        const t = res.types;
        this.dsTypesGroup.patchValue(
          {
            shortTerm: !!t.shortTerm,
            longTerm: !!t.longTerm,
            supplemental: !!t.supplemental,
            voluntaryPay: !!t.voluntaryPay,
            paidLeave: !!t.paidLeave,
            incidentOnly: !!t.incidentOnly,
            shortTermIds: [...(t.shortTermIds ?? [])],
            longTermIds: [...(t.longTermIds ?? [])],
            supplementIds: [...(t.supplementIds ?? [])],
            voluntaryPayIds: [...(t.voluntaryPayIds ?? [])],
            paidLeaveIds: [...(t.paidLeaveIds ?? [])],
            incidentOnlyIds: [...(t.incidentOnlyIds ?? [])],
            substatusIds: [...(t.substatusIds ?? [])],
            incidentSubstatusIds: [...(t.incidentSubstatusIds ?? [])],
          },
          { emitEvent: false },
        );
      }

      if (editing && res.misc) {
        const m = res.misc;
        this.dsMiscGroup.patchValue(
          {
            dateBenefitFrom: m.dateBenefitFrom
              ? new Date(m.dateBenefitFrom)
              : null,
            dateBenefitThrough: m.dateBenefitThrough
              ? new Date(m.dateBenefitThrough)
              : null,
            erisa: normalizeDsErisaFromApi(m.erisa),
            decisionDueFrom: m.decisionDueFrom
              ? new Date(m.decisionDueFrom)
              : null,
            decisionDueThrough: m.decisionDueThrough
              ? new Date(m.decisionDueThrough)
              : null,
            decisionMadeFrom: m.decisionMadeFrom
              ? new Date(m.decisionMadeFrom)
              : null,
            decisionMadeThrough: m.decisionMadeThrough
              ? new Date(m.decisionMadeThrough)
              : null,
            vendorReferral: normalizeDsVendorFromApi(m.vendorReferral),
          },
          { emitEvent: false },
        );
      }

      this.extended
        .get('includeMajorDsTypes')
        ?.setValue(true, { emitEvent: false });
      this.extended
        .get('includeMajorDsMisc')
        ?.setValue(true, { emitEvent: false });
    } finally {
      this.ensureAllDsSubstatusCheckboxControlsCreated();
      syncDsClaimSubtypeControlsEnabled(this.extended);
      this.syncSubstatusCheckboxControlsFromForm();
      this.loading = false;
      this.scheduleRefreshDsSubstatusFromQueryForm();
    }
  }
}
